import { FC, useCallback } from 'react';

import { toCSV } from 'utils/clickme';
import { FILE_DATE_WITH_TIME_FORMAT } from 'utils/constants/date';
import { formatDate } from 'utils/date';

import multicreationUnavailableDownloadButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/auction/multicreation/multicreation_unavailable_download_button_click';
import { ActionBar, BottomSheet, Button, Modal, Text } from '@hh.ru/magritte-ui';

import useTrls from 'hooks/translation/useTrls';

import { MagritteModalHeader } from 'components/common/MagritteModalHeader/MagritteModalHeader';
import { NotAvailableForCreationVacancy } from 'components/MultiCreationAuction/VacanciesTable/NotAvailableForCreationModal/NotAvailableForCreationVacancy/NotAvailableForCreationVacancy';

import { AuctionCampaignStatus } from 'store/models/auctionData';
import { AuctionCampaignCreationStatus } from 'store/models/multiCreation';

export interface NotAvailableVacancy {
    vacancyId: number;
    name?: string;
    areaName?: string;
    auctionCampaignCreationStatus?: AuctionCampaignCreationStatus;
    campaignAuctionStatus?: AuctionCampaignStatus | null;
    isUnknownVacancy: boolean;
}

interface NotAvailableForCreationModalProps {
    isVisible: boolean;
    hideModal: () => void;
    notAvailableVacancies: NotAvailableVacancy[];
}

const TrlKeys = {
    title: 'MultiCreationAuction.notAvailableForCreationModal.title',
    downloadButton: 'MultiCreationAuction.notAvailableForCreationModal.button.excel',
    cancelButton: 'MultiCreationAuction.notAvailableForCreationModal.button.cancel',
    status: {
        disabled: 'MultiCreationAuction.notAvailableForCreationModal.status.disabled',
        campaignCreated: 'MultiCreationAuction.notAvailableForCreationModal.status.campaignCreated',
        campaignStopped: 'MultiCreationAuction.notAvailableForCreationModal.status.campaignStopped',
    },
    tableHeader: {
        vacancyId: 'MultiCreationAuction.notAvailableForCreationBanner.table.header.vacancyId',
        vacancyName: 'MultiCreationAuction.notAvailableForCreationBanner.table.header.vacancyName',
        areaName: 'MultiCreationAuction.notAvailableForCreationBanner.table.header.areaName',
        status: 'MultiCreationAuction.notAvailableForCreationBanner.table.header.status',
    },
};

const STOPPED_STATUSES = [
    AuctionCampaignStatus.CampaignLowBalance,
    AuctionCampaignStatus.AccountLowBalance,
    AuctionCampaignStatus.InactiveDueToEndDate,
    AuctionCampaignStatus.Blocked,
];

export const NotAvailableForCreationModal: FC<NotAvailableForCreationModalProps> = ({
    isVisible,
    hideModal,
    notAvailableVacancies,
}) => {
    const trls = useTrls();

    const getVacancyStatus = useCallback(
        (
            campaignCreationStatus: AuctionCampaignCreationStatus | undefined,
            campaignAuctionStatus: AuctionCampaignStatus | undefined | null
        ) => {
            if (campaignCreationStatus === AuctionCampaignCreationStatus.Disabled) {
                return trls(TrlKeys.status.disabled);
            }
            if (
                campaignCreationStatus === AuctionCampaignCreationStatus.CampaignCreated &&
                campaignAuctionStatus &&
                STOPPED_STATUSES.includes(campaignAuctionStatus)
            ) {
                return trls(TrlKeys.status.campaignStopped);
            }
            if (campaignCreationStatus === AuctionCampaignCreationStatus.CampaignCreated) {
                return trls(TrlKeys.status.campaignCreated);
            }
            return trls(TrlKeys.status.disabled);
        },
        [trls]
    );

    const onCsvClick = useCallback(() => {
        multicreationUnavailableDownloadButtonClick();

        const date = formatDate(new Date(), FILE_DATE_WITH_TIME_FORMAT);
        const headersCSV = [
            trls(TrlKeys.tableHeader.vacancyId),
            trls(TrlKeys.tableHeader.vacancyName),
            trls(TrlKeys.tableHeader.areaName),
            trls(TrlKeys.tableHeader.status),
        ];

        const items = notAvailableVacancies.map(
            ({ vacancyId, name, areaName, auctionCampaignCreationStatus, campaignAuctionStatus }) => {
                return {
                    [trls(TrlKeys.tableHeader.vacancyId)]: vacancyId,
                    [trls(TrlKeys.tableHeader.vacancyName)]: name || '',
                    [trls(TrlKeys.tableHeader.areaName)]: areaName || '',
                    [trls(TrlKeys.tableHeader.status)]: getVacancyStatus(
                        auctionCampaignCreationStatus,
                        campaignAuctionStatus
                    ),
                };
            }
        );

        toCSV(`vacancies_not_available_for_creation_${date}.csv`, items, headersCSV, []);
    }, [getVacancyStatus, notAvailableVacancies, trls]);

    const downloadButton = (
        <Button
            data-qa="not-available-for-creation-modal-download"
            mode="primary"
            style="accent"
            onClick={onCsvClick}
            size="medium"
            key="not-available-for-creation-modal-download"
        >
            {trls(TrlKeys.downloadButton)}
        </Button>
    );
    const cancelButton = (
        <Button
            data-qa="not-available-for-creation-modal-cancel"
            onClick={hideModal}
            mode="secondary"
            style="accent"
            size="medium"
            key="not-available-for-creation-modal-cancel"
        >
            {trls(TrlKeys.cancelButton)}
        </Button>
    );

    const content = (
        <div>
            {notAvailableVacancies.map((vacancy, length, arr) => {
                const { vacancyId, auctionCampaignCreationStatus, campaignAuctionStatus } = vacancy;
                return (
                    <NotAvailableForCreationVacancy
                        key={vacancyId}
                        vacancy={vacancy}
                        isLastItem={arr.length - 1 === length}
                        status={getVacancyStatus(auctionCampaignCreationStatus, campaignAuctionStatus)}
                    />
                );
            })}
        </div>
    );

    return (
        <div>
            <Modal
                size="medium"
                visible={isVisible}
                onClose={hideModal}
                header={<MagritteModalHeader title={trls(TrlKeys.title)} />}
                footer={<ActionBar primaryActions={[cancelButton, downloadButton]} />}
                data-qa="not-available-for-creation-modal"
            >
                {content}
            </Modal>
            <BottomSheet
                height="content"
                visible={isVisible}
                onClose={hideModal}
                footer={<ActionBar primaryActions={[cancelButton, downloadButton]} />}
                data-qa="not-available-for-creation-sheet"
                showDivider="always"
            >
                <Text style="primary" typography="title-4-semibold">
                    {trls(TrlKeys.title)}
                </Text>
                {content}
            </BottomSheet>
        </div>
    );
};
